import React, { useEffect, useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Box,
  Grid,
  Snackbar,
  Alert,
  CircularProgress,
  useFormControl,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { setAlertFunction, showSessionExpiredAlert } from "../utils";
import { handleLogin } from "../services";
import bgImage from "../../assets/bgmaio2.png";
import { useNotifications } from "../contexts/NotificationContext";

const Login: React.FC = () => {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [errorPassword, setErrorPassword] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error">(
    "success"
  );
  const { loadNotificationsAfterLogin } = useNotifications();

  const handleCheck = async () => {
    localStorage.clear();
    setLoading(true);
    let loginReplace = login.replace(/\D/g, "");

    try {
      const responseLogin = await handleLogin(loginReplace, password);
      if (responseLogin) {
        // Carregar notificações após login bem-sucedido
        await loadNotificationsAfterLogin();
        navigate(`/home`);
      }
      setErrorPassword(false);
    } catch (error) {
      setSnackbarMessage(String(error));
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = () => {};

  const editaCpf = (e: React.ChangeEvent<HTMLInputElement>) => {
    // setLogin(handleLoginChange(e.target.value, isFone));
    setLogin(e.target.value);
  };

  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };

  useEffect(() => {
    setAlertFunction((message, severity) => {
      setSnackbarMessage(message);
      setSnackbarSeverity(severity);
      setSnackbarOpen(true);
    });

    // Verifica se foi redirecionado por sessão expirada
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("expired") === "true") {
      showSessionExpiredAlert();
      // Limpa o parâmetro da URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  const { focused } = useFormControl() || {};

  return (
    <>
      {/* Corpo */}
      <Container
        sx={{
          display: { xs: "block", sm: "flex" },
          height: "100vh",
          paddingInline: "0px !important",
          maxWidth: "100% !important",
        }}
      >
        {/* Coluna esquerda (4/6) - Imagem de fundo */}
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            width: "66.666%",
            backgroundImage: `url(${bgImage})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />

        {/* Coluna direita (2/6) - Conteúdo */}
        <Box
          sx={{
            width: { xs: "100%", md: "33.333%" },
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: 4,
          }}
        >
          {/* Logo e texto "Flow Pass" */}
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              marginBottom: 4,
            }}
          >
            <Grid>
              <img
                src="flowpet-icon-2.png"
                alt="Logo"
                style={{
                  width: "80px",
                  marginBottom: "16px",
                }}
              />
            </Grid>
            <Typography
              variant="subtitle1"
              sx={{
                color: "text.secondary",
                letterSpacing: "-0.8px",
                fontWeight: 700,
                marginTop: "-20px",
              }}
            >
              FlowPet
            </Typography>
          </Box>

          {/* Formulário de login */}
          <Box
            sx={{
              width: "100%",
              maxWidth: "400px",
              border: "1px solid lightgray",
              borderRadius: "8px",
              padding: "32px",
            }}
          >
            <TextField
              label="Insira seu login"
              variant="outlined"
              fullWidth
              color={focused ? "warning" : undefined}
              value={login}
              onChange={editaCpf}
              sx={{
                marginBottom: "16px",
              }}
            />
            <TextField
              label="Insira sua senha"
              variant="outlined"
              fullWidth
              value={password}
              onChange={(e: {
                target: { value: React.SetStateAction<string> };
              }) => setPassword(e.target.value)}
              error={errorPassword}
              helperText={errorPassword ? "Senha incorreta" : ""}
              type="password"
            />
            <Grid
              container
              sx={{
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Grid>
                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: "#FF9E5E",
                    color: "white",
                    marginTop: "15px",
                    "&:hover": { backgroundColor: "#ff8f45" },
                    width: "100%",
                  }}
                  disabled={login.length < 10 || !password}
                  onClick={handleCheck}
                >
                  {loading ? (
                    <CircularProgress
                      sx={{
                        color: "white",
                      }}
                      size={24}
                    />
                  ) : (
                    "Iniciar sessão"
                  )}
                </Button>
              </Grid>
              <Grid>
                <Button
                  variant="text"
                  sx={{
                    marginTop: "10px",
                    "&:hover": { backgroundColor: "#ffe2ce" },
                    width: "100%",
                    alignSelf: "flex-start",
                    color: "#021b1a",
                  }}
                  onClick={handlePassword}
                >
                  Esqueceu a senha?
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        <Snackbar
          open={snackbarOpen}
          autoHideDuration={5000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbarSeverity}
            sx={{ width: "100%" }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </>
  );
};

export default Login;
